import type {
  WizardDraft,
  WizardDraftStepsByType,
  WizardStep,
  WizardType,
} from "./types";

export const WIZARD_DRAFT_VERSION = 1 as const;
export const WIZARD_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const NEW_WIZARD_DRAFT_ENTITY_ID = "new";

const WIZARD_DRAFT_KEY_PREFIX = "spice:wizard-draft";

export function getWizardDraftKey(
  wizard: WizardType,
  entityId: string
): string {
  return `${WIZARD_DRAFT_KEY_PREFIX}:v${WIZARD_DRAFT_VERSION}:${wizard}:${encodeURIComponent(entityId)}`;
}

function getStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidDraftEnvelope<TWizard extends WizardType>(
  value: unknown,
  wizard: TWizard,
  entityId: string,
  now: number
): value is WizardDraft<TWizard> {
  if (!isRecord(value) || !isRecord(value.steps)) {
    return false;
  }

  return (
    value.version === WIZARD_DRAFT_VERSION &&
    value.wizard === wizard &&
    value.entityId === entityId &&
    typeof value.updatedAt === "number" &&
    Number.isFinite(value.updatedAt) &&
    value.updatedAt <= now &&
    now - value.updatedAt <= WIZARD_DRAFT_MAX_AGE_MS
  );
}

function removeStoredDraft(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be unavailable even after it was accessed successfully.
  }
}

function withWizardStep<TSteps extends object, TStep extends keyof TSteps>(
  steps: Partial<TSteps> | undefined,
  step: TStep,
  values: TSteps[TStep]
): Partial<TSteps> {
  const nextSteps: Partial<TSteps> = { ...steps };
  nextSteps[step] = values;
  return nextSteps;
}

export function readWizardDraft<TWizard extends WizardType>(
  wizard: TWizard,
  entityId: string,
  now = Date.now()
): WizardDraft<TWizard> | null {
  const storage = getStorage();
  if (!storage || !entityId) {
    return null;
  }

  const key = getWizardDraftKey(wizard, entityId);

  try {
    const rawDraft = storage.getItem(key);
    if (!rawDraft) {
      return null;
    }

    const draft: unknown = JSON.parse(rawDraft);
    if (!isValidDraftEnvelope(draft, wizard, entityId, now)) {
      removeStoredDraft(storage, key);
      return null;
    }

    return draft;
  } catch {
    removeStoredDraft(storage, key);
    return null;
  }
}

export function saveWizardStepDraft<
  TWizard extends WizardType,
  TStep extends WizardStep<TWizard>,
>(
  wizard: TWizard,
  entityId: string,
  step: TStep,
  values: WizardDraftStepsByType[TWizard][TStep],
  now = Date.now()
): boolean {
  const storage = getStorage();
  if (!storage || !entityId) {
    return false;
  }

  const previousDraft = readWizardDraft(wizard, entityId, now);
  const draft: WizardDraft<TWizard> = {
    version: WIZARD_DRAFT_VERSION,
    wizard,
    entityId,
    updatedAt: now,
    steps: withWizardStep(previousDraft?.steps, step, values),
  };

  try {
    storage.setItem(getWizardDraftKey(wizard, entityId), JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function clearWizardDraft(wizard: WizardType, entityId: string): void {
  const storage = getStorage();
  if (!storage || !entityId) {
    return;
  }

  removeStoredDraft(storage, getWizardDraftKey(wizard, entityId));
}
